/** Cloudflare Turnstile in the sign-in form: renders invisibly for most people, a checkbox for the suspicious few, and
 *  hands back a token the API verifies. Without a site key (development) it resolves to no token and the API skips the check.
 *  M11 round one: one widget per element, rendered once; a later token (the code step's "Send it again") resets that widget instead of
 *  rendering a second one into the same element, which may never answer. A token works once, so every send asks for a fresh one. */
declare global { interface Window { turnstile?: { render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; "error-callback"?: () => void; size?: string }) => string; reset: (id: string) => void } } }

export const TURNSTILE_SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function loadTurnstile(): Promise<void> {
  if (!TURNSTILE_SITE_KEY) return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.createElement("script"); s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"; s.async = true; s.onload = () => resolve(); document.head.appendChild(s);
  });
}

type Widget = { id: string; waiting: ((token: string | undefined) => void)[] };
const widgets = new WeakMap<HTMLElement, Widget>();

/** A fresh token from the element's widget: the first call renders it, every later one resets it; resolves once the visitor passed. */
export function turnstileToken(el: HTMLElement): Promise<string | undefined> {
  const t = window.turnstile;
  if (!TURNSTILE_SITE_KEY || !t) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const known = widgets.get(el);
    if (known) { known.waiting.push(resolve); t.reset(known.id); return; }
    const w: Widget = { id: "", waiting: [resolve] };
    const answer = (token: string | undefined) => { for (const r of w.waiting.splice(0)) r(token); };
    widgets.set(el, w);
    w.id = t.render(el, { sitekey: TURNSTILE_SITE_KEY!, size: "flexible", callback: (token) => answer(token), "error-callback": () => answer(undefined) });
  });
}
